import { reactive } from 'vue';
import { FolderOwnedError, FolderStorage, inspectFolder, profileFolderIn, removeFolderData, type FsDirHandle } from '@vp/core';
import { activeFolder, browserStorage, logger, reportStorageOk, storageHealth, useStorage } from './platform';
import { updateProfile, type ProfileRecord, type ProfileStorage } from './profiles/registry';
import { profileState } from './profiles/state';

/**
 * Where a profile keeps its canvases (docs/TZ-PROFILES.md 4.2): its own IndexedDB database or a folder
 * on the computer (File System Access API — Chromium browsers on computers). The place is chosen once,
 * when the profile is created, and cannot be changed afterwards; it is part of the profile's record.
 * App settings, journal and modules stay in the profile's database in any case.
 *
 * A folder is either the profile's own (its data at the root, the marker names the profile) or a
 * folder of profiles (each profile in the subfolder named by its id). The browser forgets the
 * permission to a folder when it restarts: on start the app then shows a gate (FolderGate.vue). A
 * folder that was moved can be pointed to again — only the folder with this profile's data.
 */
/** A non-empty folder with someone else's files: the canvases go into a subfolder with this name. */
const SUBFOLDER = 'Visual Platform';

export const folderSupported = typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function' && window.isSecureContext;

export const storagePlace = reactive({
  kind: 'browser' as 'browser' | 'folder',
  /** The folder's name as shown (the picked folder, or picked/subfolder). */
  name: '',
  /** The profile's own folder or a folder of profiles. */
  mode: '' as '' | 'own' | 'shared',
  /** The app cannot start on the folder yet: why. */
  gate: null as null | { reason: 'permission' | 'missing' | 'error' },
});

type FolderPlace = Extract<ProfileStorage, { kind: 'folder' }>;
let saved: FolderPlace | null = null;

const asDir = (h: FileSystemDirectoryHandle) => h as unknown as FsDirHandle;
const errName = (e: unknown) => (e as { name?: string } | null)?.name ?? 'unknown';

function profileId(): string {
  const id = profileState.current?.id;
  if (!id) throw new Error('no profile is open');
  return id;
}

/** Where a profile's files are: the folder itself, or its subfolder in a folder of profiles. */
async function dataDir(where: FolderPlace, id: string): Promise<FsDirHandle> {
  return where.mode === 'shared' ? profileFolderIn(asDir(where.handle), id) : asDir(where.handle);
}

async function permission(handle: FileSystemDirectoryHandle, ask: boolean): Promise<PermissionState> {
  const h = handle as unknown as FsPermissionHandle;
  try {
    return (await (ask ? h.requestPermission?.({ mode: 'readwrite' }) : h.queryPermission?.({ mode: 'readwrite' }))) ?? 'granted';
  } catch {
    return ask ? 'denied' : 'prompt';
  }
}

/** At start, before anything reads: open the profile's folder, or leave the gate up. */
export async function openSavedLocation(): Promise<void> {
  if (storageHealth.state === 'memory') return;
  const where = profileState.current?.storage;
  if (!where || where.kind !== 'folder' || !where.handle) return;
  saved = where;
  storagePlace.name = where.name;
  storagePlace.mode = where.mode;
  storagePlace.kind = 'folder';
  if ((await permission(where.handle, false)) !== 'granted') storagePlace.gate = { reason: 'permission' };
  else await activate(where);
}

async function activate(where: FolderPlace): Promise<boolean> {
  try {
    const folder = new FolderStorage(await dataDir(where, profileId()), browserStorage);
    await folder.init(profileId());
    await folder.probe();
    useStorage(folder);
    storagePlace.kind = 'folder';
    storagePlace.name = where.name;
    storagePlace.mode = where.mode;
    storagePlace.gate = null;
    // the browser's small quota of a private window does not concern the folder
    if (storageHealth.state === 'limited') storageHealth.state = 'ok';
    return true;
  } catch (e) {
    storagePlace.gate = { reason: errName(e) === 'NotFoundError' ? 'missing' : 'error' };
    logger.warn('storage.folder.open', { error: errName(e) });
    return false;
  }
}

/** The gate's button (a click: the browser asks the user). */
export async function grantAccess(): Promise<boolean> {
  const handle = saved?.handle ?? (activeFolder()?.root as unknown as FileSystemDirectoryHandle | undefined);
  if (!handle) return false;
  if ((await permission(handle, true)) !== 'granted') return false;
  // working already (the banner's button): saving retries by itself
  if (activeFolder()) {
    reportStorageOk();
    return true;
  }
  return saved ? activate(saved) : false;
}

export class FolderChoiceError extends Error {
  constructor(readonly reason: 'owned' | 'notThisProfile') {
    super(reason);
  }
}

/**
 * The folder picked for a new profile. `mode`: known from what is in the folder (a folder of profiles,
 * one made before profiles, someone else's files → a subfolder), or `ask` for an empty folder (the
 * person says whether it is this profile's alone or shared by profiles). Null — the picker was closed.
 */
export async function pickFolderForProfile(): Promise<{ handle: FileSystemDirectoryHandle; name: string; mode: 'own' | 'shared' | 'ask' } | null> {
  if (!window.showDirectoryPicker) return null;
  let picked: FileSystemDirectoryHandle;
  try {
    picked = await window.showDirectoryPicker({ id: 'vp-storage', mode: 'readwrite' });
  } catch (e) {
    if (errName(e) === 'AbortError') return null;
    throw e;
  }
  const st = await inspectFolder(asDir(picked));
  if (st.kind === 'set') return { handle: picked, name: picked.name, mode: 'shared' };
  // another profile's folder is not shared with a new one
  if (st.kind === 'ours') {
    if (st.profile) throw new FolderChoiceError('owned');
    return { handle: picked, name: picked.name, mode: 'own' };
  }
  if (st.kind === 'other') {
    // someone else's files: ours go beside them in a subfolder, never mixed in
    const sub = await picked.getDirectoryHandle(SUBFOLDER, { create: true });
    const inner = await inspectFolder(asDir(sub));
    if (inner.kind === 'set') return { handle: sub, name: `${picked.name}/${SUBFOLDER}`, mode: 'shared' };
    if (inner.kind === 'ours' && inner.profile) throw new FolderChoiceError('owned');
    return { handle: sub, name: `${picked.name}/${SUBFOLDER}`, mode: 'own' };
  }
  return { handle: picked, name: picked.name, mode: 'ask' };
}

/**
 * The profile's folder was moved or renamed: point to it again (the gate). Only a folder with this
 * profile's data is accepted — the place itself does not change.
 */
export async function relocateFolder(): Promise<boolean> {
  if (!window.showDirectoryPicker) return false;
  const id = profileId();
  let picked: FileSystemDirectoryHandle;
  try {
    picked = await window.showDirectoryPicker({ id: 'vp-storage', mode: 'readwrite' });
  } catch (e) {
    if (errName(e) === 'AbortError') return false;
    throw e;
  }
  const st = await inspectFolder(asDir(picked));
  let where: FolderPlace | null = null;
  if (st.kind === 'ours' && st.profile === id) where = { kind: 'folder', mode: 'own', handle: picked, name: picked.name };
  else if (st.kind === 'set') {
    const inside = await asDir(picked).getDirectoryHandle(id).catch(() => null);
    if (inside && (await inspectFolder(inside)).kind === 'ours') where = { kind: 'folder', mode: 'shared', handle: picked, name: picked.name };
  }
  if (!where) throw new FolderChoiceError('notThisProfile');
  profileState.current = await updateProfile(id, { storage: where });
  saved = where;
  return activate(where);
}

/**
 * The profile is deleted: remove its files from its folder (its own folder: only what the platform
 * wrote; a folder of profiles: its subfolder). Asks for access if needed (call it from a click).
 * Returns false when the files could not be reached (they stay, the user is told).
 */
export async function removeProfileFolder(p: ProfileRecord): Promise<boolean> {
  if (p.storage.kind !== 'folder') return true;
  const where = p.storage;
  try {
    if ((await permission(where.handle, false)) !== 'granted' && (await permission(where.handle, true)) !== 'granted') return false;
    if (where.mode === 'shared') {
      await asDir(where.handle)
        .removeEntry(p.id, { recursive: true })
        .catch((e: unknown) => {
          if (errName(e) !== 'NotFoundError') throw e;
        });
    } else await removeFolderData(asDir(where.handle));
    return true;
  } catch (e) {
    logger.warn('profile.folder.remove', { error: errName(e) });
    return false;
  }
}

export { FolderOwnedError };
